// Automatic activation of an update that was already waiting when the page started (see CheckUpdates).

const LAST_AUTO_UPDATE_KEY = 'ksa:sw-auto-update-at';
const RELOAD_LOOP_GUARD_MS = 30 * 1000;
const SERVICE_WORKER_ANSWER_TIMEOUT_MS = 3000;
const ACTIVATION_TIMEOUT_MS = 5000;

let userHasInteracted = false;
const markInteraction = () => {
    userHasInteracted = true;
};

// Registered when the module loads (app start): any click, tap or key press counts as "user is active".
if (typeof window !== 'undefined') {
    window.addEventListener('pointerdown', markInteraction, { capture: true, once: true });
    window.addEventListener('keydown', markInteraction, { capture: true, once: true });
}

export const hasUserInteracted = () => userHasInteracted;

/** Test helper */
export const resetUserInteraction = () => {
    userHasInteracted = false;
};

const readLastAutoUpdate = () => {
    try {
        return Number(sessionStorage.getItem(LAST_AUTO_UPDATE_KEY)) || 0;
    } catch (e) {
        return 0;
    }
};

export const wasAutoUpdatedRecently = (now = Date.now()) => now - readLastAutoUpdate() < RELOAD_LOOP_GUARD_MS;

export const rememberAutoUpdate = (now = Date.now()) => {
    try {
        sessionStorage.setItem(LAST_AUTO_UPDATE_KEY, String(now));
    } catch (e) {
        // Without sessionStorage the loop guard is missing – then do not auto-update at all.
        return false;
    }
    return true;
};

/**
 * Asks the waiting service worker to activate itself if this tab is the only one of the app.
 * Resolves false if another tab is open or the worker does not answer in time (e.g. an older
 * service worker version that does not know the message yet).
 */
export const requestActivationIfSoleClient = (worker: ServiceWorker, timeoutMs = SERVICE_WORKER_ANSWER_TIMEOUT_MS) =>
    new Promise<boolean>((resolve) => {
        const channel = new MessageChannel();
        const timer = setTimeout(() => {
            channel.port1.close();
            resolve(false);
        }, timeoutMs);
        channel.port1.onmessage = (event) => {
            clearTimeout(timer);
            channel.port1.close();
            resolve(!!event.data?.activating);
        };
        worker.postMessage({ type: 'SKIP_WAITING_IF_SOLE_CLIENT' }, [channel.port2]);
    });

/** Resolves true once the worker is activated, false after the timeout. */
export const waitForActivation = (worker: ServiceWorker, timeoutMs = ACTIVATION_TIMEOUT_MS) =>
    new Promise<boolean>((resolve) => {
        if (worker.state === 'activated') {
            resolve(true);
            return;
        }
        const timer = setTimeout(() => resolve(false), timeoutMs);
        worker.addEventListener('statechange', () => {
            if (worker.state === 'activated') {
                clearTimeout(timer);
                resolve(true);
            }
        });
    });
